/**
 * Piper kit primitives — tiny building blocks shared across the redesign.
 * All colors come from the `piper` token object; no one-off values.
 */
import { forwardRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { piper } from '../../theme/theme';

export const PIPER_MARK_SRC = '/assets/piper-mark.png';

/** Tone → { color, bg, border } map used by chips/tags across the kit. */
export const TONES = {
  green: { color: piper.green, bg: piper.greenBg, border: piper.greenBorder },
  greenSoft: { color: piper.green2, bg: piper.greenBg2, border: piper.greenBorder },
  red: { color: piper.redDark, bg: piper.redTintBg2, border: piper.redTintBorder },
  amber: { color: piper.amber, bg: piper.amberBg, border: 'transparent' },
  blue: { color: piper.blue, bg: piper.blueBg, border: 'transparent' },
  slate: { color: '#fff', bg: piper.slate, border: 'transparent' },
  neutral: { color: piper.inkSecondary, bg: piper.subtleFill, border: 'transparent' },
};

/** Uppercase 10–11px 700-weight muted micro-label. */
export const MicroLabel = ({ children, sx, ...rest }) => (
  <Typography component="div" sx={{ ...piper.microLabel, ...sx }} {...rest}>
    {children}
  </Typography>
);

/** 20px-radius tinted status pill. */
export const StatusChip = ({ tone = 'neutral', children, sx, ...rest }) => {
  const t = TONES[tone] || TONES.neutral;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        borderRadius: `${piper.radius.pill}px`,
        px: 1.1,
        py: '2.5px',
        fontSize: '11px',
        fontWeight: 600,
        lineHeight: 1.4,
        color: t.color,
        backgroundColor: t.bg,
        border: t.border !== 'transparent' ? `1px solid ${t.border}` : 'none',
        whiteSpace: 'nowrap',
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
};

/**
 * CountPill — tiny flat numeric badge used next to tab labels and list rows
 * (e.g. unsaved-edit count on the Loan File tab, document status counts).
 * Same tone palette as StatusChip but denser and never outlined.
 */
export const CountPill = forwardRef(({ tone = 'neutral', children, sx, ...rest }, ref) => {
  const t = TONES[tone] || TONES.neutral;
  return (
    <Box
      ref={ref}
      component="span"
      sx={{
        px: 0.6,
        minWidth: 15,
        borderRadius: '8px',
        textAlign: 'center',
        fontSize: '10px',
        fontWeight: 700,
        lineHeight: '15px',
        display: 'inline-block',
        backgroundColor: t.bg,
        color: t.color,
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
});
CountPill.displayName = 'CountPill';

/**
 * ProvenanceTag — labels anything Piper touched. Glyph + text, never color
 * alone (accessibility requirement from the prototype).
 * tone: green = Piper did it / verified · slate = filled by Piper · red = needs you
 */
export const ProvenanceTag = ({ glyph = '◆', label, tone = 'slate', sx }) => {
  const colors = {
    green: piper.green2,
    slate: piper.slate,
    red: piper.redDark,
    amber: piper.amber,
    blue: piper.blue,
  };
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.4,
        fontSize: '10.5px',
        fontWeight: 600,
        color: colors[tone] || piper.slate,
        whiteSpace: 'nowrap',
        ...sx,
      }}
    >
      <span aria-hidden="true">{glyph}</span>
      {label}
    </Box>
  );
};

/** Piper avatar — brand mark asset with letter-P fallback if the image fails to load. */
export const PiperAvatar = ({ size = 32, sx }) => {
  const [useFallback, setUseFallback] = useState(false);

  const shellSx = {
    width: size,
    height: size,
    borderRadius: '50%',
    flexShrink: 0,
    ...sx,
  };

  if (useFallback) {
    return (
      <Box
        aria-hidden="true"
        sx={{
          ...shellSx,
          backgroundColor: piper.red,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 800,
          fontSize: size * 0.5,
          lineHeight: 1,
        }}
      >
        P
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={PIPER_MARK_SRC}
      alt=""
      aria-hidden="true"
      draggable={false}
      onError={() => setUseFallback(true)}
      sx={{
        ...shellSx,
        objectFit: 'contain',
        display: 'block',
        backgroundColor: '#fff',
        p: `${Math.max(2, Math.round(size * 0.08))}px`,
        boxSizing: 'border-box',
      }}
    />
  );
};

/** Live status line — green dot + text ("● watching all 24 loans"). */
export const LiveStatus = ({ children, sx }) => (
  <Typography
    component="div"
    sx={{ fontSize: '11px', fontWeight: 600, color: piper.green2, display: 'flex', alignItems: 'center', gap: 0.5, ...sx }}
  >
    <Box component="span" aria-hidden="true" sx={{ fontSize: '9px', lineHeight: 1 }}>●</Box>
    {children}
  </Typography>
);

/** Chat bubble with the flat corner pointing at the speaker. */
export const ChatBubble = ({ children, tone = 'neutral', sx }) => (
  <Box
    sx={{
      borderRadius: piper.radius.bubble,
      backgroundColor: tone === 'green' ? piper.greenBg2 : piper.subtleFill,
      border: tone === 'green' ? `1px solid ${piper.greenBorder}` : 'none',
      px: 1.5,
      py: 1.25,
      fontSize: '12.5px',
      lineHeight: 1.55,
      color: piper.ink,
      ...sx,
    }}
  >
    {children}
  </Box>
);

/** Hairline-bordered flat white card. */
export const PiperCard = ({ children, sx, ...rest }) => (
  <Box
    sx={{
      backgroundColor: piper.surface,
      border: `1px solid ${piper.hairlineStrong}`,
      borderRadius: `${piper.radius.panel}px`,
      ...sx,
    }}
    {...rest}
  >
    {children}
  </Box>
);

export { piper };
